import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends HTMLElement {
  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    // --- READ PHASE ---
    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    // --- COMPUTE ---
    let left = undefined;
    if (rect.right > viewportWidth) {
      left = `-${rect.right - viewportWidth + OFFSET}px`;
    } else if (rect.left < 0) {
      left = `${Math.abs(rect.left) + OFFSET}px`;
    }

    // --- WRITE PHASE (batched, no reads after this) ---
    this.style.top = OFFSET + 'px';
    if (left !== undefined) {
      this.style.left = left;
    }

    this.#mutationObserver.takeRecords();
  };

  #mutationObserver = new MutationObserver(this.#updatePosition);

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
    }
  }, 100);

  connectedCallback() {
    window.addEventListener('resize', this.#resizeListener, { passive: true });

    requestIdleCallback(() => {
      this.#updatePosition();
      this.#mutationObserver.observe(this, { attributes: true });
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#resizeListener);
    this.#mutationObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
